import './App.css';
import { useNavigate } from 'react-router-dom';
import { useEffect, useMemo, useRef } from 'react';

import mainImage from './assets/images/raymong-main.png';
import TitleComponent from './components/TitleComponent';
import Footer from './components/Footer';
import arrow from './assets/images/arrow.png';

// Input images
import mongsoon1 from './assets/images/mongsoon/1.png';
import mongsoon2 from './assets/images/mongsoon/2.png';
import mongsoon3 from './assets/images/mongsoon/3.png';
import ray1 from './assets/images/ray/1.jpeg';
import ray2 from './assets/images/ray/2.jpeg';
import ray3 from './assets/images/ray/3.jpeg';

function App() {
    let navigate = useNavigate();

    // Refs for scrolling to specific images
    const inputCarouselRef = useRef(null);
    const conceptRefs = useRef({
        job: null,
        studio: null,
        season: null,
        trip: null,
        'water-paint': null,
        'oil-paint': null,
    });

    // Cache all images for both pets and all concepts
    const imageCache = useMemo(() => {
        const cache = {};
        const conceptRanges = {
            studio: { start: 1, end: 10 },
            trip: { start: 11, end: 20 },
            season: { start: 21, end: 30 },
            job: { start: 31, end: 40 },
            'water-paint': { start: 41, end: 50 },
            'oil-paint': { start: 51, end: 60 },
        };

        ['dog', 'cat'].forEach((petType) => {
            cache[petType] = {};
            const folder =
                petType === 'dog' ? 'mongsoon-results' : 'ray-results';

            Object.keys(conceptRanges).forEach((concept) => {
                const range = conceptRanges[concept];
                const images = [];

                for (let i = range.start; i <= range.end; i++) {
                    try {
                        images.push(
                            require(`./assets/images/${folder}/${i}.jpg`)
                        );
                    } catch (e) {
                        console.error(
                            `Failed to load image: ${folder}/${i}.jpg`
                        );
                    }
                }

                cache[petType][concept] = images;
            });
        });

        return cache;
    }, []);

    // Combined input images: 3 dog + 3 cat
    const allInputImages = useMemo(() => {
        return [
            { src: mongsoon1, alt: '강아지 입력 1', pet: 'dog' },
            { src: mongsoon2, alt: '강아지 입력 2', pet: 'dog' },
            { src: mongsoon3, alt: '강아지 입력 3', pet: 'dog' },
            { src: ray1, alt: '고양이 입력 1', pet: 'cat' },
            { src: ray2, alt: '고양이 입력 2', pet: 'cat' },
            { src: ray3, alt: '고양이 입력 3', pet: 'cat' },
        ];
    }, []);

    // Preload all images
    useEffect(() => {
        const concepts = [
            'job',
            'studio',
            'season',
            'trip',
            'water-paint',
            'oil-paint',
        ];
        const pets = ['dog', 'cat'];

        concepts.forEach((concept) => {
            pets.forEach((pet) => {
                const images = imageCache[pet][concept];
                images.forEach((src) => {
                    const img = new Image();
                    img.src = src;
                });
            });
        });

        // Preload input images
        allInputImages.forEach(({ src }) => {
            const img = new Image();
            img.src = src;
        });
    }, [imageCache, allInputImages]);

    const routeChange = (isInternal, path, e) => {
        e.preventDefault();
        if (isInternal) {
            let newPath = path;
            navigate(newPath);
        } else {
            window.open(path);
        }
    };

    // Jump carousel to specific pet's position
    const jumpCarouselToPosition = (carouselEl, imageIndex) => {
        if (!carouselEl) return;

        const track = carouselEl.querySelector('.carousel-track');
        if (!track) return;

        // Each image is 280px + 20px gap = 300px
        const imageWidth = 300;
        const translateValue = -(imageIndex * imageWidth);

        // Set CSS variable for animation start position
        track.style.setProperty('--scroll-start', `${translateValue}px`);

        // Restart animation
        track.style.animation = 'none';
        void track.offsetWidth; // Force reflow
        track.style.animation = '';
    };

    // Handle pet button click
    const handlePetClick = (pet) => {
        const inputIndex = pet === 'dog' ? 0 : 3;
        const conceptIndex = pet === 'dog' ? 0 : 10;

        // Jump input carousel
        jumpCarouselToPosition(inputCarouselRef.current, inputIndex);

        // Jump all concept carousels
        [
            'job',
            'studio',
            'season',
            'trip',
            'water-paint',
            'oil-paint',
        ].forEach((concept) => {
            jumpCarouselToPosition(conceptRefs.current[concept], conceptIndex);
        });
    };

    // Input Images Section Component
    const InputSection = () => {
        return (
            <div className="input-showcase-section">
                <div className="block">
                    <div className="colored-bold">이미지를 넣으면</div>
                    <div className="light" style={{ marginTop: '20px' }}>
                        반려동물의 다양한 표정과 각도에서 찍은 1~5장의 사진을
                        <br />
                        선택해주세요.
                    </div>

                    <div
                        className="pet-toggle"
                        style={{ marginTop: '30px', marginBottom: '30px' }}
                    >
                        <button
                            className="toggle-btn"
                            onClick={() => handlePetClick('dog')}
                        >
                            강아지
                        </button>
                        <button
                            className="toggle-btn"
                            onClick={() => handlePetClick('cat')}
                        >
                            고양이
                        </button>
                    </div>

                    <div className="carousel-container" ref={inputCarouselRef}>
                        <div className="carousel-track">
                            {/* Original images */}
                            {allInputImages.map((img, idx) => (
                                <img
                                    key={`input-original-${idx}`}
                                    src={img.src}
                                    alt={img.alt}
                                    className="carousel-image"
                                />
                            ))}
                            {/* Duplicate images for infinite loop */}
                            {allInputImages.map((img, idx) => (
                                <img
                                    key={`input-duplicate-${idx}`}
                                    src={img.src}
                                    alt={img.alt}
                                    className="carousel-image"
                                />
                            ))}
                        </div>
                    </div>

                    <div className="arrow-down-section">
                        <img
                            src={arrow}
                            alt="arrow down"
                            className="arrow-down-icon"
                        />
                    </div>

                    <div className="light" style={{ marginTop: '20px' }}>
                        선택한 컨셉에 맞는 10장의 아트가 생성됩니다!
                    </div>
                </div>
            </div>
        );
    };

    // Concept Section Component - shows 20 images (10 dog + 10 cat)
    const ConceptSection = ({ concept, title, description }) => {
        const dogImages = imageCache.dog[concept];
        const catImages = imageCache.cat[concept];
        const allImages = [...dogImages, ...catImages];

        return (
            <div className="concept-section">
                <div className="concept-header">
                    <h2 className="concept-title">{title}</h2>
                    <p className="concept-description">{description}</p>
                </div>

                <div className="concept-output-content">
                    <div
                        className="carousel-container"
                        ref={(el) => (conceptRefs.current[concept] = el)}
                    >
                        <div className="carousel-track">
                            {/* Original images */}
                            {allImages.map((img, idx) => (
                                <img
                                    key={`${concept}-original-${idx}`}
                                    src={img}
                                    alt={`${title} ${idx + 1}`}
                                    className="carousel-image"
                                />
                            ))}
                            {/* Duplicate images for infinite loop */}
                            {allImages.map((img, idx) => (
                                <img
                                    key={`${concept}-duplicate-${idx}`}
                                    src={img}
                                    alt={`${title} ${idx + 1}`}
                                    className="carousel-image"
                                />
                            ))}
                        </div>
                    </div>
                </div>
            </div>
        );
    };

    return (
        <div className="app">
            {/* Hero Section */}
            <div className="landing-content">
                <TitleComponent
                    style={{ marginRight: '20px' }}
                    subtitle={'AI가 그려주는 동물 아트 한 보따리'}
                />
                <button
                    className="get-started-button"
                    onClick={(e) => {
                        routeChange(true, '/main', e);
                    }}
                >
                    GET STARTED
                </button>

                <div className="landing-image">
                    <img src={mainImage} alt="레이몽 메인" />
                </div>
            </div>

            {/* Input Images Section */}
            <InputSection />

            {/* Concept Introduction */}
            <div className="service-intro-section">
                <div className="block">
                    <div className="colored-bold">
                        6가지 컨셉 중 하나를 선택하세요!
                    </div>
                </div>
            </div>

            {/* Concept Sections */}
            <div className="concepts-container">
                <ConceptSection
                    concept="season"
                    title="시즌"
                    description="시즌별 테마 컨셉 (계절, 명절, 기념일)"
                />
                <ConceptSection
                    concept="job"
                    title="직업"
                    description="다양한 직업군 컨셉 (모험가, 뮤지션 등)"
                />

                <ConceptSection
                    concept="studio"
                    title="스튜디오"
                    description="프로필 사진 같은 전문 스튜디오 촬영 스타일"
                />
                <ConceptSection
                    concept="trip"
                    title="여행"
                    description="세계 명소에서 찍은 것 같은 여행 컨셉"
                />
                <ConceptSection
                    concept="water-paint"
                    title="수채화"
                    description="물감이 번지는 부드러운 수채화 스타일"
                />
                <ConceptSection
                    concept="oil-paint"
                    title="유화"
                    description="진하고 입체적인 유화 스타일"
                />
            </div>

            {/* Pricing Section */}
            <div className="white-background">
                <div className="block">
                    <div className="mid-title">컨셉 1개당 10장 한 보따리</div>
                    <div className="mid-title" style={{ marginTop: '20px' }}>
                        <span
                            style={{
                                color: '#ff7b07',
                                fontWeight: 'bold',
                                fontSize: '36px',
                            }}
                        >
                            3,900원
                        </span>
                    </div>

                    <div className="center-button">
                        <div className="price-instruction">
                            시간은 약 5분 ~ 20분 가량 소요됩니다
                        </div>
                        <button
                            className="get-started-button main-bottom-button"
                            onClick={(e) => {
                                routeChange(true, '/main', e);
                            }}
                        >
                            GET STARTED
                        </button>
                    </div>
                </div>
            </div>

            {/* Privacy Section */}
            <div
                className="block"
                style={{ textAlign: 'center', fontFamily: 'Suite-light' }}
            >
                레이몽은 데이터 보호를 위해 최선을 다하고 있으며 반려동물의
                사진을 유출하거나 추가 학습 자료로 사용하지 않습니다
            </div>

            <Footer />
        </div>
    );
}

export default App;
